import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { readResultsKey } from '../utils/drillTelemetry';
import './drill-1.css';

type DrillEvent = {
  token: string;
  ts: string;
  event: string;
  method?: string;
  ip?: string;
  ua?: string;
  path?: string;
  referrer?: string;
};

type EventsResponse = {
  events?: DrillEvent[];
  error?: string;
};

function formatTs(ts: string): string {
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'medium',
    timeZone: 'Europe/Berlin',
  }).format(date);
}

const DrillResultsPage: React.FC = () => {
  const initialKey = typeof window === 'undefined' ? '' : readResultsKey(window.location.search);
  const [key, setKey] = useState(initialKey);
  const [draft, setDraft] = useState(initialKey);
  const [events, setEvents] = useState<DrillEvent[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!key) return;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch(`/api/drill-events?k=${encodeURIComponent(key)}`, { signal: controller.signal })
      .then(async (res) => {
        const body = (await res.json()) as EventsResponse;
        if (!res.ok) {
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        if (!controller.signal.aborted) {
          setEvents(body.events || []);
        }
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError' || controller.signal.aborted) return;
        setEvents([]);
        setError(err.message || 'Ergebnisse konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [key]);

  const grouped = useMemo(() => {
    const map = new Map<string, DrillEvent[]>();
    for (const event of events) {
      const list = map.get(event.token) || [];
      list.push(event);
      map.set(event.token, list);
    }
    return [...map.entries()].map(([token, list]) => ({
      token,
      list: [...list].sort((a, b) => a.ts.localeCompare(b.ts)),
      opens: list.filter((item) => item.event === 'open').length,
      clicks: list.filter((item) => item.event === 'click').length,
      views: list.filter((item) => item.event === 'view').length,
    }));
  }, [events]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setKey(draft.trim());
  };

  return (
    <div className="drill-1" lang="de">
      <div className="drill-1-tape" aria-hidden="true" />
      <main className="drill-1-wrap">
        <p className="drill-1-kicker">
          <span className="drill-1-stamp">Intern</span>
          <span>Drill-1 · nur mit Auswertungsschlüssel</span>
        </p>
        <h1>Wer hat geöffnet und geklickt?</h1>
        <p>
          Die Tabelle zeigt Tokens, keine Namen. Die Zuordnung Token → Person steht nur in der
          lokalen Sendeliste auf Rons Rechner. Ein „open“ heißt: das Zählpixel wurde geladen
          (Proxy möglich). Ein „click“ heißt: der Tracking-Link wurde aufgerufen (Scanner
          möglich). Ein „view“ heißt: die Übungsseite hat JavaScript ausgeführt.
        </p>
        <form className="drill-1-form" onSubmit={onSubmit}>
          <label htmlFor="drill-key">Auswertungsschlüssel</label>
          <input
            id="drill-key"
            name="k"
            type="password"
            autoComplete="off"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button type="submit">Laden</button>
        </form>
        {loading && <p>Lade Ereignisse…</p>}
        {error && <p className="drill-1-error">{error}</p>}
        {!loading && !error && key && grouped.length === 0 && (
          <p>Noch keine Ereignisse für diese Übung.</p>
        )}
        {grouped.map((group) => (
          <section key={group.token}>
            <h2>
              {group.token}
              <span style={{ fontWeight: 400, fontSize: '1rem', marginLeft: '0.75rem' }}>
                open {group.opens} · click {group.clicks} · view {group.views}
              </span>
            </h2>
            <table className="drill-1-results-table">
              <thead>
                <tr>
                  <th>Zeit (Berlin)</th>
                  <th>Ereignis</th>
                  <th>Methode</th>
                  <th>IP</th>
                  <th>User-Agent</th>
                </tr>
              </thead>
              <tbody>
                {group.list.map((item) => (
                  <tr key={`${item.token}-${item.ts}-${item.event}-${item.ua || ''}`}>
                    <td>{formatTs(item.ts)}</td>
                    <td>{item.event}</td>
                    <td>{item.method || ''}</td>
                    <td>{item.ip || ''}</td>
                    <td>{item.ua || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
        <p className="drill-1-foot">
          Nicht als Beweis für eine einzelne Person lesen, wenn BCC, Weiterleitung oder ein
          Mailscanner im Spiel sind.
        </p>
      </main>
      <div className="drill-1-tape" aria-hidden="true" />
    </div>
  );
};

export default DrillResultsPage;
